import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import {
  Permission,
  PermissionDataContext,
  PermissionDataContextValue,
  PermissionTemplate,
  usePermissionDataProvider,
} from 'model/PermissionDataContext';
import { useCallback, useEffect, useMemo, useState } from 'react';

export { PermissionDataContext, usePermissionDataProvider };
export type { Permission, PermissionDataContextValue, PermissionTemplate };

/**
 * Loads the permissions from the API.
 */
export function PermissionDataProvider({ children }: { children: React.ReactNode }) {
  const { ready, jwt, getAction } = useApi();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionTemplates, setPermissionTemplates] = useState<PermissionTemplate[]>([]);

  const organizationPermissions = useMemo(() => permissions.filter((p) => p.isOrganization), [permissions]);

  const departmentPermissions = useMemo(() => permissions.filter((p) => p.isDepartment), [permissions]);

  const globalPermissions = useMemo(() => permissions.filter((p) => p.isGlobal), [permissions]);

  const organizationPermissionTemplates = useMemo(
    () => permissionTemplates.filter((t) => t.isOrganization),
    [permissionTemplates]
  );
  const departmentPermissionTemplates = useMemo(
    () => permissionTemplates.filter((t) => t.isDepartment),
    [permissionTemplates]
  );

  /**
   * Returns the permission by its ID or slug.
   */
  const getPermission = useCallback(
    (idOrSlug: number | string) =>
      permissions.find((permission) => permission.permissionId === idOrSlug || permission.slug === idOrSlug),
    [permissions]
  );

  const getPermissionTemplate = useCallback(
    (idOrSlug: number | string) =>
      permissionTemplates.find(
        (permissionTemplate: any) =>
          permissionTemplate.permissionTemplateId === idOrSlug || permissionTemplate.slug === idOrSlug
      ),
    [permissionTemplates]
  );

  const getDefaultPermissionTemplate = useCallback(
    () => permissionTemplates.find(({ slug }) => slug === 'no-template')!,
    [permissionTemplates]
  );

  useEffect(() => {
    if (ready && jwt) {
      const permissionsGetListAction = getAction('AuthListPermissions');
      permissionsGetListAction()
        .then((permissions) => {
          setPermissions(Array.isArray(permissions) ? permissions : []);
        })
        .catch(panic);

      const permissionTemplatesGetListAction = getAction('AuthListPermissionTemplates');
      permissionTemplatesGetListAction()
        .then((permissionTemplates) => {
          setPermissionTemplates(Array.isArray(permissionTemplates) ? permissionTemplates : []);
        })
        .catch(panic);
    }
  }, [ready, jwt]);

  return (
    <PermissionDataContext.Provider
      value={{
        permissions,
        permissionTemplates,
        globalPermissions,
        organizationPermissions,
        departmentPermissions,
        organizationPermissionTemplates,
        departmentPermissionTemplates,
        getPermission,
        getPermissionTemplate,
        getDefaultPermissionTemplate,
      }}
    >
      {children}
    </PermissionDataContext.Provider>
  );
}
